import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatNumber, formatDate, statusBadgeClass, statusLabel } from '../utils/formatters';
import toast from 'react-hot-toast';
import { Search, Plus, Eye, ChevronLeft, ChevronRight, Package, AlertTriangle, ClipboardCheck, Check, Save } from 'lucide-react';

function OpnameDetail({ opname, onBack, onApprove }) {
  const [counts, setCounts] = useState({});
  const [saving, setSaving] = useState(false);

  const handleCountChange = (itemId, value) => {
    setCounts((prev) => ({ ...prev, [itemId]: value === '' ? '' : Number(value) }));
  };

  const handleSubmitCounts = async () => {
    setSaving(true);
    try {
      const items = (opname.items || []).map((item) => ({
        product_id: item.product_id,
        physical_count: counts[item.id] ?? item.physical_count ?? item.quantity,
      })).filter((item) => item.physical_count !== '' && item.physical_count !== null);

      const res = await api.put(`/inventory/stock-opname/${opname.id}/count`, { items });
      if (res.data.success) {
        toast.success('Hitung fisik stok berhasil disimpan');
        onBack();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <button onClick={onBack} className="text-sm text-primary-800 hover:text-primary-900 mb-2 flex items-center gap-1">
            <ChevronLeft className="h-4 w-4" /> Kembali ke Daftar
          </button>
          <h1 className="page-title">Detail Opname: {opname.doc_number}</h1>
          <p className="page-subtitle">
            Gudang: {opname.warehouse?.name || opname.warehouse_name || '-'} | 
            Tanggal: {formatDate(opname.date || opname.created_at)}
          </p>
        </div>
        <div className="flex gap-2">
          {opname.status === 'in_progress' && (
            <button onClick={handleSubmitCounts} disabled={saving} className="btn-primary">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Menyimpan...' : 'Simpan Hitungan'}
            </button>
          )}
          {opname.status === 'pending_approval' && (
            <button onClick={() => onApprove(opname)} className="btn-success">
              <Check className="h-4 w-4 mr-2" /> Setujui
            </button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="stat-card">
          <div className="stat-icon stat-icon-brand">
            <Package className="h-6 w-6" />
          </div>
          <div>
            <p className="stat-label">Total Item</p>
            <p className="stat-value">{formatNumber(opname.items?.length || 0)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon stat-icon-success">
            <Check className="h-6 w-6" />
          </div>
          <div>
            <p className="stat-label">Cocok</p>
            <p className="stat-value">
              {formatNumber(opname.items?.filter((i) => (i.physical_count ?? i.quantity) === i.quantity).length || 0)}
            </p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon stat-icon-danger">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div>
            <p className="stat-label">Selisih</p>
            <p className="stat-value">
              {formatNumber(opname.items?.filter((i) => (i.physical_count ?? i.quantity) !== i.quantity).length || 0)}
            </p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon stat-icon-neutral">
            <ClipboardCheck className="h-6 w-6" />
          </div>
          <div>
            <p className="stat-label">Status</p>
            <p className="stat-value">
              <span className={`badge ${statusBadgeClass(opname.status)}`}>{statusLabel(opname.status)}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="card">
        <div className="card-body">
          <h3 className="text-base font-semibold text-surface-900 dark:text-white mb-4">Item Opname</h3>
          {(!opname.items || opname.items.length === 0) ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">Tidak ada item dalam sesi opname ini.</p>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Nama Produk</th>
                    <th className="text-right">Stok Sistem</th>
                    <th className="text-right">Hitung Fisik</th>
                    <th className="text-right">Selisih</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {opname.items.map((item) => {
                    const physicalCount = counts[item.id] ?? item.physical_count ?? item.quantity;
                    const diff = physicalCount - item.quantity;
                    const isEditing = opname.status === 'in_progress';
                    return (
                      <tr key={item.id}>
                        <td>
                          <span className="font-mono text-xs text-gray-600 dark:text-gray-400">{item.sku || item.product?.sku || '-'}</span>
                        </td>
                        <td>
                          <span className="font-medium text-gray-900 dark:text-white">{item.product_name || item.product?.name || '-'}</span>
                        </td>
                        <td className="text-right">
                          <span className="text-sm text-gray-700 dark:text-gray-200">{formatNumber(item.quantity)}</span>
                        </td>
                        <td className="text-right">
                          {isEditing ? (
                            <input
                              type="number"
                              className="input w-24 text-right"
                              value={counts[item.id] ?? item.physical_count ?? ''}
                              onChange={(e) => handleCountChange(item.id, e.target.value)}
                              placeholder={item.quantity}
                            />
                          ) : (
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{formatNumber(physicalCount)}</span>
                          )}
                        </td>
                        <td className="text-right">
                          <span className={`text-sm font-medium ${diff > 0 ? 'text-success-600' : diff < 0 ? 'text-danger-600' : 'text-gray-500'}`}>
                            {diff > 0 ? '+' : ''}{formatNumber(diff)}
                          </span>
                        </td>
                        <td>
                          {diff === 0 ? (
                            <span className="badge badge-success">Cocok</span>
                          ) : (
                            <span className="badge badge-danger">Selisih</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function StockOpnamePage() {
  const { hasPermission } = useAuth();
  const [opnames, setOpnames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewingOpname, setViewingOpname] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [form, setForm] = useState({ warehouse_id: '', date: new Date().toISOString().split('T')[0], notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchOpnames();
  }, [pagination.page, filters]);

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchOpnames = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/inventory/stock-opname', { params });
      if (res.data.success) {
        setOpnames(res.data.data?.opnames || res.data.data?.items || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const res = await api.get('/warehouses', { params: { limit: 1000 } });
      setWarehouses(res.data.data?.warehouses || res.data.data || []);
    } catch (error) {
      // Silently fail
    }
  };

  const fetchOpnameDetail = async (id) => {
    try {
      const res = await api.get(`/inventory/stock-opname/${id}`);
      if (res.data.success) {
        setViewingOpname(res.data.data);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/inventory/stock-opname', form);
      if (res.data.success) {
        toast.success('Sesi opname berhasil dibuat');
        setShowCreateModal(false);
        setForm({ warehouse_id: '', date: new Date().toISOString().split('T')[0], notes: '' });
        fetchOpnames();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleView = (opname) => {
    fetchOpnameDetail(opname.id);
  };

  const handleComplete = async (opname) => {
    if (!confirm('Yakin ingin menyelesaikan sesi opname ini?')) return;
    try {
      const res = await api.post(`/inventory/stock-opname/${opname.id}/complete`);
      if (res.data.success) {
        toast.success('Opname berhasil diselesaikan');
        fetchOpnames();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleApprove = async (opname) => {
    if (!confirm('Yakin ingin menyetujui sesi opname ini? Penyesuaian stok akan diterapkan.')) return;
    try {
      const res = await api.post(`/inventory/stock-opname/${opname.id}/approve`);
      if (res.data.success) {
        toast.success('Opname berhasil disetujui');
        setViewingOpname(null);
        fetchOpnames();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  // If viewing a detail
  if (viewingOpname) {
    return (
      <OpnameDetail
        opname={viewingOpname}
        onBack={() => setViewingOpname(null)}
        onComplete={handleComplete}
        onApprove={handleApprove}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Stok Opname</h1>
          <p className="page-subtitle">Kelola sesi opname stok dan penyesuaian inventaris</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <Plus className="h-4 w-4 mr-2" /> Buat Opname Baru
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari nomor dokumen..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchOpnames()}
              />
            </div>
            <div>
              <select
                className="input"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Semua Status</option>
                <option value="draft">Draf</option>
                <option value="in_progress">Berlangsung</option>
                <option value="pending_approval">Menunggu Persetujuan</option>
                <option value="completed">Selesai</option>
                <option value="approved">Disetujui</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={fetchOpnames} className="btn-primary btn-sm">Cari</button>
              <button onClick={() => setFilters({ search: '', status: '' })} className="btn-secondary btn-sm">Reset</button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-32" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-36" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-20" />
                </div>
              ))}
            </div>
          </div>
        ) : opnames.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <ClipboardCheck className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data Opname</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada sesi opname stok yang dibuat.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>No. Dokumen</th>
                    <th>Tanggal</th>
                    <th>Gudang</th>
                    <th>Status</th>
                    <th>Dibuat Oleh</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {opnames.map((opname) => (
                    <tr key={opname.id}>
                      <td>
                        <span className="font-medium text-gray-900 dark:text-white">{opname.doc_number}</span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDate(opname.date || opname.created_at)}</span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-700 dark:text-gray-200">{opname.warehouse?.name || opname.warehouse_name || '-'}</span>
                      </td>
                      <td>
                        <span className={`badge ${statusBadgeClass(opname.status)}`}>{statusLabel(opname.status)}</span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-700 dark:text-gray-200">{opname.created_by?.name || opname.created_by_name || '-'}</span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleView(opname)}
                            className="p-1.5 text-primary-900 hover:bg-primary-50 dark:hover:bg-primary-950/50 rounded"
                            title="Lihat Detail"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {opname.status === 'pending_approval' && hasPermission('approve_stock_opname') && (
                            <button
                              onClick={() => handleApprove(opname)}
                              className="p-1.5 text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20 rounded"
                              title="Setujui"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Buat Sesi Opname Baru</h3>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="label">Gudang <span className="text-danger-600">*</span></label>
                <select
                  className="input"
                  value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
                  required
                >
                  <option value="">Pilih Gudang</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Tanggal <span className="text-danger-600">*</span></label>
                <input
                  type="date"
                  className="input"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="label">Catatan</label>
                <textarea
                  className="input min-h-[80px]"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Catatan opname (opsional)"
                  rows={3}
                />
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Membuat...' : 'Buat Sesi Opname'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
